import React, { useState } from 'react';
import styled from 'styled-components';
import { Fade, Slide, AttentionSeeker } from 'react-awesome-reveal';
import Swal from 'sweetalert2';

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1a1a1a;
  background-color: #f9fafb;
  overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.section`
  min-height: 60vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  position: relative;
`;

const HeroContent = styled.div`
  max-width: 800px;

  h1 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    letter-spacing: -0.025em;
    line-height: 1.2;
    
    span {
      color: #38bdf8;
    }
  }

  p {
    font-size: 1.25rem;
    color: #cbd5e1;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
`;

// Program Benefits Section
const BenefitsSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 3rem;
  color: #0f172a;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: #2563eb;
    margin: 0.75rem auto 0;
    border-radius: 2px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  margin-bottom: 4rem;
`;

const BenefitCard = styled.div`
  background: #ffffff;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #f3f4f6;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 0.75rem;
  }

  p {
    color: #64748b;
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

// Criteria / Mandatory Step Callout
const CriteriaSection = styled.section`
  background-color: #eff6ff;
  padding: 4rem 2rem;
  border-top: 1px solid #bfdbfe;
  border-bottom: 1px solid #bfdbfe;
`;

const CriteriaContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;

  h2 {
    color: #1e40af;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    color: #1e3a8a;
    font-size: 1.15rem;
    line-height: 1.6;
    max-width: 700px;
    margin: 0 auto 2rem;
  }
`;

// Form Registration Section
const RegisterSection = styled.section`
  padding: 5rem 2rem;
  background: #f8fafc;
`;

const FormWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
  background: #ffffff;
  padding: 3rem;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 600;
    font-size: 0.9rem;
    color: #334155;
  }

  input, select, textarea {
    padding: 0.85rem 1rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 1rem;
    background-color: #f8fafc;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #2563eb;
      background-color: #ffffff;
      box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    }
  }
`;

const CourseSelectCard = styled.div`
  background: #f0fdf4;
  border: 2px dashed #22c55e;
  padding: 1.5rem;
  border-radius: 12px;
  margin-top: 0.5rem;

  label {
    color: #14532d;
  }
  
  select {
    background-color: #ffffff;
    border-color: #bbf7d0;
    font-weight: 600;
    color: #166534;
  }
`;

const SubmitButton = styled.button`
  background: #2563eb;
  color: #ffffff;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background: #1d4ed8;
  }
`;

// --- COMPONENT IMPLEMENTATION ---

export default function ITRegistration() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    institution: '',
    courseOfStudy: '',
    duration: '',
  
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  // 1. Trigger Loading State Alert
  Swal.fire({
    title: 'Processing Application...',
    text: 'Please wait while we log your details.',
    allowOutsideClick: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    const response = await fetch('https://elexdontech.com/api/submit_it_registration.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const result = await response.json();

    if (result.success) {
      // 2. Success Alert
      Swal.fire({
        title: 'Registration Initiated!',
        text: result.message,
        icon: 'success',
      
        confirmButtonColor: '#2563eb'
      });

      // Clear form data state
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        institution: '',
        courseOfStudy: '',
        duration: '',
      
      });
    } else {
      // 3. Server-side Validation/Error Alert
      Swal.fire({
        title: 'Application Failed',
        text: result.error || 'Something went wrong.',
        icon: 'error',
        confirmButtonText: 'Review Form',
        confirmButtonColor: '#ef4444'
      });
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    
    // 4. Network/Runtime Exception Failure Alert
    Swal.fire({
      title: 'Connection Error',
      text: 'An unexpected network failure occurred. Please check your connection and try again.',
      icon: 'error',
      confirmButtonText: 'Dismiss',
      confirmButtonColor: '#64748b'
    });
  }
};

  return (
    <PageContainer>
      {/* 1. Hero Section */}
      <HeroSection>
        <HeroContent>
          <Fade direction="up" cascade damping={0.2} triggerOnce>
            <h1>Launch Your Tech Career with Our <span>Industrial Training Program</span></h1>
            <p>
              Get hands-on ecosystem experience, work on production-level projects, 
              and clear your path to becoming a world-class engineer.
            </p>
            <a href="#register-form" style={{
              background: '#2563eb', color: '#fff', padding: '0.85rem 2rem', 
              borderRadius: '8px', textDecoration: 'none', fontWeight: '600'
            }}>
              Apply Now
            </a>
          </Fade>
        </HeroContent>
      </HeroSection>

      {/* 2. Inspirational & Informational Section */}
      <BenefitsSection>
        <SectionTitle>Why Intern With Us?</SectionTitle>
       <Grid>
  <Fade direction="up" triggerOnce>
    <BenefitCard>
      <img 
      src="https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=600&q=80"
        alt="Real-world Projects" 
      />
      <h3>Real-World Codebases</h3>
      <p>Skip the sandbox tasks. Our IT students sit right in production pipelines, shipping features that active customers interact with daily.</p>
    </BenefitCard>
  </Fade>

  <Fade direction="up" triggerOnce>
    <BenefitCard>
      <img 
        src="https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=600&q=80" 
        alt="Mentorship" 
      />
      <h3>Elite Engineering Mentorship</h3>
      <p>Paired 1-on-1 with industry veterans, you’ll master high-level clean code practices, system architectures, and Agile environments.</p>
    </BenefitCard>
  </Fade>

  <Fade direction="up" triggerOnce>
    <BenefitCard>
      <img 
        src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80" 
        alt="Career Pathway" 
      />
      <h3>Fast-Tracked Post-Grad Hiring</h3>
      <p>We treat our IT program as an extended pipeline. Exceptional performers walk away with direct return offers for full-time engineering roles.</p>
    </BenefitCard>
  </Fade>
</Grid>
      </BenefitsSection>

      {/* 3. Criteria & Milestone Nudge */}
      <CriteriaSection>
        <CriteriaContainer>
          <AttentionSeeker effect="pulse" triggerOnce>
            <h2>Admission Criteria & Policy</h2>
          </AttentionSeeker>
          <p>
            To maintain our high engineering standards and ensure absolute baseline readiness for production pipelines, 
            <strong> all IT candidates are required to select and purchase one of our courses as a prerequisite for acceptance.</strong>. 
          
          </p>
        </CriteriaContainer>
      </CriteriaSection>

      {/* 4. Interactive Form Section */}
      <RegisterSection id="register-form">
        <SectionTitle>Industrial Training Application Form</SectionTitle>
        <Slide direction="up" triggerOnce>
          <FormWrapper>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <label htmlFor="fullName">Full Name</label>
                <input 
                  type="text" id="fullName" name="fullName" required 
                  value={formData.fullName} onChange={handleChange} placeholder="John Doe" 
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email Address</label>
                <input 
                  type="email" id="email" name="email" required 
                  value={formData.email} onChange={handleChange} placeholder="johndoe@example.com" 
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="phone">Phone Number</label>
                <input 
                  type="tel" id="phone" name="phone" required 
                  value={formData.phone} onChange={handleChange} placeholder="+234..." 
                />
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormGroup>
                  <label htmlFor="institution">Institution</label>
                  <input 
                    type="text" id="institution" name="institution" required 
                    value={formData.institution} onChange={handleChange} placeholder="University / Poly" 
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="courseOfStudy">Course of Study</label>
                  <input 
                    type="text" id="courseOfStudy" name="courseOfStudy" required 
                    value={formData.courseOfStudy} onChange={handleChange} placeholder="Computer Science" 
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <label htmlFor="duration">Required Placement Duration</label>
                <select id="duration" name="duration" value={formData.duration} onChange={handleChange} required>
                    <option value="">-- Select --</option>
                  <option value="3-months">3 Months</option>
                  <option value="6-months">6 Months</option>
                  <option value="1-year">1 Year</option>
                </select>
              </FormGroup>

    

              <SubmitButton type="submit">Proceed to Enrollment & Register</SubmitButton>
            </Form>
          </FormWrapper>
        </Slide>
      </RegisterSection>
    </PageContainer>
  );
}